import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchSuggestion } from '@pylot-data/hooks/search/use-search-suggestion'
import { reportSynonymConversion } from '@pylot-data/hooks/search/synonymConversion'
import { isPdpUrl, navigateToPdp } from '@lib/hooks/product/pdp/pdpEvents'
import { pdpHrefFromCanonical, toPdpHref } from '@lib/utils/pdpHref'
import { useEmptyAutocomplete } from '@pylot-data/hooks/search/use-empty-autocomplete'
import s from './SearchAutocomplete.module.scss'
import cn from 'classnames'
import Link from 'next/link'
import { LoadingDots } from '@corratech/pylot-ui'
import { useRouter } from 'next/router'
import Image from '@corratech/pylot-image'
import IconChevronLeft from '../../icons/IconChevronLeft'

const formatAutocompletePrice = (value: number): string =>
    `$${value.toLocaleString('es-CL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`
import IconChevronRight from '../../icons/IconChevronRight'

type CarouselItemPrice = {
    finalPrice: number
    regularPrice: number
    currency: string
    hasDiscount: boolean
    maxFinalPrice?: number
    maxRegularPrice?: number
}
type autoCompleteType = {
    searchTerm: string
    searchPageURL: string
    closeAutocomplete: () => void
    isForPWA?: boolean
    onSearchSubmit?: () => void
    layout?: 'dropdown' | 'card' | 'sheet'
}

export const renderHighlightedTerm = (
    term: string,
    searchTerm: string
): React.ReactNode => {
    const query = searchTerm.trim()
    if (!query) return term

    const lowerTerm = term.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const matchIndex = lowerTerm.indexOf(lowerQuery)
    if (matchIndex === -1) return term

    const before = term.slice(0, matchIndex)
    const match = term.slice(matchIndex, matchIndex + query.length)
    const after = term.slice(matchIndex + query.length)

    return (
        <>
            {before}
            <strong>{match}</strong>
            {after}
        </>
    )
}

const SearchAutocomplete: FC<autoCompleteType> = ({
    searchTerm,
    searchPageURL,
    closeAutocomplete,
    isForPWA = false,
    onSearchSubmit,
    layout = 'dropdown'
}) => {
    const carouselRef = useRef<HTMLUListElement | null>(null)
    const arrowCleanupRef = useRef<(() => void) | null>(null)
    const [showPrevArrow, setShowPrevArrow] = useState(false)
    const [showNextArrow, setShowNextArrow] = useState(false)
    const [sheetShowAll, setSheetShowAll] = useState(false)

    const setCarouselNode = useCallback((node: HTMLUListElement | null) => {
        arrowCleanupRef.current?.()
        arrowCleanupRef.current = null
        carouselRef.current = node
        if (!node) return

        const updateArrows = () => {
            const maxScroll = node.scrollWidth - node.clientWidth
            setShowPrevArrow(node.scrollLeft > 1)
            setShowNextArrow(maxScroll - node.scrollLeft > 1)
        }

        updateArrows()
        node.addEventListener('scroll', updateArrows, { passive: true })
        const observer = new ResizeObserver(updateArrows)
        observer.observe(node)
        arrowCleanupRef.current = () => {
            node.removeEventListener('scroll', updateArrows)
            observer.disconnect()
        }
    }, [])
    const { t } = useTranslation('common')
    const router = useRouter()
    const isShortSearch = searchTerm.trim().length < 3
    const {
        products,
        isValidating: isValidatingSuggestions,
        error: suggestionsError
    } = useSearchSuggestion(
        searchTerm,
        {
            revalidateOnFocus: false,
            keepPreviousData: true
        },
        !isShortSearch
    )
    const hasSuggestions = Boolean(products?.items && products.items.length > 0)
    const suggestionsResolved = !isShortSearch && products !== undefined
    const suggestionsEmpty =
        suggestionsResolved &&
        (!products?.items || products.items.length === 0)
    const shouldFetchEmpty = isShortSearch || suggestionsEmpty
    const {
        emptyAutocomplete,
        isValidating: isValidatingEmpty,
        error: emptyError
    } = useEmptyAutocomplete(
        {
            revalidateOnFocus: false,
            keepPreviousData: true
        },
        shouldFetchEmpty
    )

    const containerClass = isForPWA
        ? 'search-container-mobile'
        : 'search-container'
    const cardLayout = layout === 'card'
    const sheetLayout = layout === 'sheet'

    useEffect(() => {
        router.events.on('routeChangeComplete', closeAutocomplete)
        return () => {
            router.events.off('routeChangeComplete', closeAutocomplete)
        }
    }, [router.events, closeAutocomplete])

    useEffect(() => () => arrowCleanupRef.current?.(), [])

    useEffect(() => {
        setSheetShowAll(false)
    }, [searchTerm])

    const openFullResults = () => {
        const query = searchTerm.trim()
        if (query.length > 2) {
            closeAutocomplete()
            if (onSearchSubmit) {
                onSearchSubmit()
                return
            }
            router.push(
                {
                    pathname: searchPageURL,
                    query: { q: query }
                },
                undefined,
                { shallow: router.pathname !== searchPageURL }
            )
            return
        }
        setSheetShowAll(true)
    }

    const waitingSuggestions =
        !isShortSearch && products === undefined && !suggestionsError
    const waitingEmpty =
        shouldFetchEmpty && emptyAutocomplete === undefined && !emptyError

    if (
        isValidatingSuggestions ||
        isValidatingEmpty ||
        waitingSuggestions ||
        waitingEmpty
    ) {
        return (
            <div
                className={cn(
                    s[containerClass],
                    cardLayout && s.autocompleteCard,
                    sheetLayout && s.autocompleteSheet
                )}
            >
                <LoadingDots />
            </div>
        )
    }

    const hasEmptyCategories = Boolean(emptyAutocomplete?.categories?.length)
    const siteBaseUrl = 'https://www.casaideas.cl'

    const normalizeImageUrl = (url?: string | null) => {
        if (!url) return '/product-img-placeholder.svg'
        if (url.startsWith('http')) return url
        if (url.startsWith('//')) return `https:${url}`
        if (url.startsWith('/placeholder/')) return `${siteBaseUrl}${url}`
        return url
    }

    const normalizeProductUrl = (urlKeyOrUrl?: string | null) =>
        toPdpHref(urlKeyOrUrl)

    const normalizeCategoryUrl = (url?: string | null) => {
        if (!url?.trim()) return '#'
        const path = url
            .replace(/^https?:\/\/[^/]+/, '')
            .replace(/^\//, '')
            .replace(/\/$/, '')
        if (!path) return '#'
        if (path.toLowerCase().startsWith('categoria/')) return `/${path}`
        return `/categoria/${path}`
    }

    const productUrlFromCanonical = (canonicalUrl?: string | null) =>
        pdpHrefFromCanonical(canonicalUrl)
    const handleImageError = (
        e: React.SyntheticEvent<HTMLImageElement, Event>
    ) => {
        const img = e.currentTarget
        if (img.dataset.fallbackApplied === '1') return
        img.dataset.fallbackApplied = '1'
        img.onerror = null
        img.src = '/product-img-placeholder.svg'
    }
    const emptyProducts =
        emptyAutocomplete?.products?.map((product) => {
            const priceObj = product?.price
            const priceRange = (product as any)?.price_range
            const minPrice = priceRange?.minimum_price ?? priceObj
            const maxPrice = priceRange?.maximum_price
            const finalVal =
                minPrice?.final_price?.value ??
                priceObj?.final_price?.value ??
                0
            const regularVal =
                minPrice?.regular_price?.value ??
                priceObj?.regular_price?.value ??
                0
            const discount = minPrice?.discount ?? priceObj?.discount
            const hasDiscount =
                (discount?.percent_off ?? 0) > 0 ||
                (discount?.amount_off ?? 0) > 0 ||
                (regularVal > 0 && finalVal < regularVal)
            const currency =
                minPrice?.final_price?.currency ??
                priceObj?.final_price?.currency ??
                minPrice?.regular_price?.currency ??
                priceObj?.regular_price?.currency ??
                'CLP'
            return {
                key: product?.sku ?? '',
                label: product?.name || 'Cosmetiquero XL',
                url: normalizeProductUrl(
                    (product as any)?.url_key || product?.url
                ),
                image: normalizeImageUrl(product?.small_image),
                price:
                    priceObj || minPrice
                        ? ({
                              finalPrice: finalVal,
                              regularPrice: regularVal,
                              currency,
                              hasDiscount,
                              maxFinalPrice: maxPrice?.final_price?.value,
                              maxRegularPrice: maxPrice?.regular_price?.value
                          } as CarouselItemPrice)
                        : undefined
            }
        }) || []
    const carouselItems = emptyProducts

    // Card desktop: sin término, "Ver más" lleva a la búsqueda completa.
    const openCardResults = () => {
        if (searchTerm.trim().length > 2) {
            openFullResults()
            return
        }
        closeAutocomplete()
        router.push(searchPageURL)
    }

    const renderCarousel = (
        items: typeof carouselItems,
        title: string,
        titleClassName?: string,
        showCardSeeMore = false
    ) => (
        <div className={s['autocomplete-carousel']}>
            <div className={s['autocomplete-carousel-header']}>
                <span
                    className={cn(
                        s['autocomplete-carousel-title'],
                        titleClassName
                    )}
                >
                    {title}
                </span>
                {cardLayout && showCardSeeMore && (
                    <button
                        type="button"
                        className={s.cardSeeMore}
                        onClick={openCardResults}
                    >
                        {t('Search see more', { defaultValue: 'Ver más' })}
                    </button>
                )}
            </div>
            <div className={s['autocomplete-carousel-track']}>
                {showPrevArrow && (
                    <button
                        type="button"
                        className={s['autocomplete-carousel-arrow']}
                        aria-label={t('slider.previous')}
                        onClick={() => {
                            carouselRef.current?.scrollBy({
                                left: -220,
                                behavior: 'smooth'
                            })
                        }}
                    >
                        <IconChevronLeft />
                    </button>
                )}
                <ul
                    ref={setCarouselNode}
                    className={s['autocomplete-carousel-list']}
                >
                    {(sheetLayout && !sheetShowAll
                        ? items.slice(0, 4)
                        : items
                    ).map((item, index) => {
                        const price: CarouselItemPrice | undefined =
                            'price' in item
                                ? (item as { price?: CarouselItemPrice }).price
                                : undefined
                        const content = (
                            <>
                                <Image
                                    className={s['autocomplete-carousel-image']}
                                    src={item.image}
                                    alt={item.label ?? ''}
                                    width={150}
                                    height={150}
                                    onError={handleImageError}
                                />
                                <span
                                    className={s['autocomplete-carousel-text']}
                                >
                                    {item.label}
                                </span>
                                {price && (
                                    <div
                                        className={
                                            s['autocomplete-carousel-price']
                                        }
                                    >
                                        <span
                                            className={cn(
                                                s[
                                                    'autocomplete-carousel-price-final'
                                                ],
                                                price.hasDiscount &&
                                                    s[
                                                        'autocomplete-carousel-price-final-discount'
                                                    ]
                                            )}
                                        >
                                            {price.hasDiscount
                                                ? formatAutocompletePrice(
                                                      price.finalPrice
                                                  )
                                                : price.maxFinalPrice != null &&
                                                    price.maxFinalPrice !==
                                                        price.finalPrice
                                                  ? `${formatAutocompletePrice(
                                                        price.finalPrice
                                                    )} - ${formatAutocompletePrice(
                                                        price.maxFinalPrice
                                                    )}`
                                                  : formatAutocompletePrice(
                                                        price.finalPrice
                                                    )}
                                        </span>
                                        {price.hasDiscount && (
                                            <span
                                                className={
                                                    s[
                                                        'autocomplete-carousel-price-regular'
                                                    ]
                                                }
                                            >
                                                {formatAutocompletePrice(
                                                    price.regularPrice
                                                )}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </>
                        )

                        return (
                            <li
                                key={item.key || `${item.label}-${index}`}
                                className={s['autocomplete-carousel-item']}
                            >
                                {item.url ? (
                                    <a
                                        href={item.url}
                                        className={
                                            s['autocomplete-carousel-link']
                                        }
                                        onClick={(e) => {
                                            const isSpecialClick =
                                                e.metaKey ||
                                                e.ctrlKey ||
                                                e.shiftKey ||
                                                e.button === 1

                                            // MSE: search product click for Live Search attribution
                                            const sku =
                                                item.key &&
                                                !item.key.startsWith(
                                                    'missing-'
                                                ) &&
                                                !item.key.startsWith(
                                                    'suggestion-'
                                                )
                                                    ? item.key
                                                    : undefined
                                            if (sku) {
                                                const mse = (window as any)
                                                    .magentoStorefrontEvents
                                                if (mse) {
                                                    mse.publish.searchProductClick(
                                                        'livesearch-popover',
                                                        sku
                                                    )
                                                    // Feed behavior_score for synonym-driven searches.
                                                    void reportSynonymConversion(
                                                        'PRODUCT_CLICK'
                                                    )
                                                }
                                            }

                                            if (!isSpecialClick) {
                                                e.preventDefault()
                                                closeAutocomplete()
                                                if (isPdpUrl(item.url)) {
                                                    navigateToPdp(
                                                        router,
                                                        item.url
                                                    )
                                                } else {
                                                    router.push(item.url)
                                                }
                                            }
                                        }}
                                    >
                                        {content}
                                    </a>
                                ) : (
                                    <div
                                        className={
                                            s['autocomplete-carousel-link']
                                        }
                                    >
                                        {content}
                                    </div>
                                )}
                            </li>
                        )
                    })}
                </ul>
                {showNextArrow && (
                    <button
                        type="button"
                        className={`${s['autocomplete-carousel-arrow']} ${s['autocomplete-carousel-arrow-right']}`}
                        aria-label={t('slider.next')}
                        onClick={() => {
                            carouselRef.current?.scrollBy({
                                left: 220,
                                behavior: 'smooth'
                            })
                        }}
                    >
                        <IconChevronRight />
                    </button>
                )}
            </div>
            {sheetLayout &&
                items.length > 0 &&
                (!sheetShowAll || items.length <= 4) && (
                    <button
                        type="button"
                        className={s.sheetSeeMore}
                        onClick={openFullResults}
                    >
                        {t('Search see more', { defaultValue: 'Ver más' })}
                    </button>
                )}
        </div>
    )

    const renderEmptyAutocomplete = () => (
        <div
            className={cn(
                s.autocomplete,
                isForPWA && s.autocompleteMobile,
                cardLayout && s.autocompleteCard,
                sheetLayout && s.autocompleteSheet
            )}
        >
            <div className={s['autocomplete-terms']}>
                {hasEmptyCategories && (
                    <>
                        <span className={s['autocomplete-carousel-title']}>
                            {t('Search categories', {
                                defaultValue: 'Categorías'
                            })}
                        </span>
                        <ul className={s['autocomplete-terms-list']}>
                        {emptyAutocomplete?.categories?.map((category) => (
                            <li key={category?.id ?? category?.url}>
                                <Link
                                    href={normalizeCategoryUrl(category?.url)}
                                    className={s['autocomplete-term']}
                                >
                                    {category?.name}
                                </Link>
                            </li>
                        ))}
                        </ul>
                    </>
                )}
            </div>
            {carouselItems.length > 0 &&
                renderCarousel(
                    carouselItems,
                    t('Star products'),
                    s['autocomplete-carousel-title-stars'],
                    true
                )}
            {!isShortSearch && !sheetLayout && (
                <button
                    className={cn(
                        'link',
                        'secondary',
                        s['see-all-occurrences-btn'],
                        s['autocomplete-full-width']
                    )}
                    onClick={() => {
                        closeAutocomplete()
                        if (onSearchSubmit) {
                            onSearchSubmit()
                        } else {
                            router.push(
                                {
                                    pathname: searchPageURL,
                                    query: searchTerm ? { q: searchTerm } : {}
                                },
                                undefined,
                                {
                                    shallow: router.pathname !== searchPageURL
                                }
                            )
                        }
                    }}
                >
                    {`${t('See all occurencies of')} ${searchTerm}`}
                </button>
            )}
        </div>
    )

    if (shouldFetchEmpty && emptyAutocomplete) {
        return renderEmptyAutocomplete()
    }

    if (hasSuggestions) {
        const productSearch = products as {
            facets?: Array<{
                attribute?: string
                title?: string
                buckets?: Array<{ title?: string }>
            }>
        }
        const productTypeFacet = productSearch?.facets?.find(
            (f) => f?.attribute === 'tipo_de_producto'
        )
        const productTypeBuckets =
            productTypeFacet?.buckets?.filter((b) => b?.title) ?? []

        const suggestionCarouselItems = (products.items ?? []).map(
            (product, index) => {
                const canonicalUrl = (product as { canonical_url?: string })
                    ?.canonical_url
                const urlFromKey = product?.url_key
                    ? normalizeProductUrl(product.url_key)
                    : ''
                const url =
                    urlFromKey ||
                    productUrlFromCanonical(canonicalUrl) ||
                    searchPageURL
                const minPrice = (product as any)?.price_range?.minimum_price
                const maxPrice = (product as any)?.price_range?.maximum_price
                const finalVal = minPrice?.final_price?.value ?? 0
                const regularVal = minPrice?.regular_price?.value ?? 0
                const maxFinalVal = maxPrice?.final_price?.value
                const maxRegularVal = maxPrice?.regular_price?.value
                const hasDiscount =
                    (minPrice?.discount?.amount_off ?? 0) > 0 ||
                    (minPrice?.discount?.percent_off ?? 0) > 0 ||
                    (regularVal > 0 && finalVal < regularVal)
                const currency =
                    minPrice?.final_price?.currency ??
                    minPrice?.regular_price?.currency ??
                    'CLP'

                return {
                    key: product?.sku ?? `suggestion-${index}`,
                    label: product?.name || 'Cosmetiquero XL',
                    url,
                    image: normalizeImageUrl(product?.small_image?.url),
                    price: minPrice
                        ? ({
                              finalPrice: finalVal,
                              regularPrice: regularVal,
                              currency,
                              hasDiscount,
                              maxFinalPrice: maxFinalVal,
                              maxRegularPrice: maxRegularVal
                          } as CarouselItemPrice)
                        : undefined
                }
            }
        )

        return (
            <div
                className={cn(
                    s.autocomplete,
                    isForPWA && s.autocompleteMobile,
                    cardLayout && s.autocompleteCard,
                    sheetLayout && s.autocompleteSheet
                )}
            >
                <div className={s['autocomplete-terms']}>
                    <a
                        href={`${searchPageURL}?q=${encodeURIComponent(
                            searchTerm
                        )}`}
                        onClick={() => closeAutocomplete()}
                    >
                        <h5 className={s['autocomplete-heading']}>
                            {t('SUGERENCIAS')}
                        </h5>
                    </a>
                    <ul className={s['autocomplete-terms-list']}>
                        {productTypeBuckets.map((bucket) => {
                            const suggestionQuery = encodeURIComponent(
                                bucket.title ?? ''
                            )
                            return (
                                <li key={bucket.title}>
                                    <a
                                        href={`${searchPageURL}?q=${suggestionQuery}`}
                                        className={s['autocomplete-term']}
                                        onClick={() => closeAutocomplete()}
                                    >
                                        {bucket.title}
                                    </a>
                                </li>
                            )
                        })}
                    </ul>
                </div>
                {suggestionCarouselItems.length > 0 &&
                    renderCarousel(
                        suggestionCarouselItems,
                        t('Product results'),
                        s['autocomplete-carousel-title-results']
                    )}
                {!isShortSearch && !sheetLayout && (
                    <button
                        className={cn(
                            'link',
                            'secondary',
                            s['see-all-occurrences-btn'],
                            s['autocomplete-full-width']
                        )}
                        onClick={() => {
                            closeAutocomplete()
                            if (onSearchSubmit) {
                                onSearchSubmit()
                            } else {
                                router.push(
                                    {
                                        pathname: searchPageURL,
                                        query: searchTerm
                                            ? { q: searchTerm }
                                            : {}
                                    },
                                    undefined,
                                    {
                                        shallow:
                                            router.pathname !== searchPageURL
                                    }
                                )
                            }
                        }}
                    >
                        {`${t('See all occurencies of')} ${searchTerm}`}
                    </button>
                )}
            </div>
        )
    } else {
        // Solo "sin resultados" cuando la API respondió vacío o falló de forma definitiva.
        return <div className={s[containerClass]}>{t('¡Sin resultados!')}</div>
    }
}

export default SearchAutocomplete
